import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {RootState} from "commons/store/store";
import {EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead} from "types/api.types";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import {HalResource} from "types/halResource.types";
import {PurpleItem, StatsItem} from "../components/DashboardStats/StatsTypes";

export interface EcrfState {
    ecrfVisits: any; // todo model
    stats: { item: StatsItem[]; researchStartedAt?: string; totalItems: number; };
    statsEcrves: HalResource<any>;
    ecrves: {
        itemsPerPage: number;
        totalItems: number;
        data: EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead[];
    };
    isLoading: boolean;
    error: any;
}

const initialState: EcrfState = {
    ecrves: {
        itemsPerPage: 0,
        totalItems: 0,
        data: [],
    },
    ecrfVisits: [],
    stats: { item: [], researchStartedAt: undefined, totalItems: 0 },
    statsEcrves: {
      _links: {},
      itemsPerPage: 0,
      totalItems: 0
    },
    isLoading: false,
    error: null
};

type fetchEcrvesParams = {
    page: number;
};

export const fetchEcrves = createAsyncThunk(
    "dashboard/fetchEcrves",
    async ({page}: fetchEcrvesParams) => {
        const response = await axiosSecureInstance.get<HalResource<EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead[]>>("/api/ecrves", {
            headers: {
                accept: "application/hal+json",
            },
            params: {
                pagination: true,
                page,
                itemsPerPage: 30
            },
        });
        return response.data;
    }
);

export const fetchEcrfVisits = createAsyncThunk(
  "dashboard/fetchEcrfVisits", async (id: string) => {
    const response = await axiosSecureInstance.get<any>(
        `/api/ecrves/${id}/visits`
    );
    return response.data;
});

export const fetchStats = createAsyncThunk(
  "dashboard/fetchStats", async () => {
    const response = await axiosSecureInstance.get<any>(`/api/stats`);
    return response.data;
});

export const fetchStatsEcrves = createAsyncThunk(
  "dashboard/fetchStatsEcrves",
  async (config: any, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.get<HalResource<any>>(`/api/ecrves/statistics`, config);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const addEcrf = createAsyncThunk("users/addEcrf", async () => {
    try {
        const response =
            await axiosSecureInstance.post<EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead>(
                `/api/ecrves`,
                {}
            );
        return response.data;
    } catch (error: any) {
        const {response} = error;
        const {request, ...errorObject} = response; // take everything but 'request'
        alert(errorObject?.data?.detail)
        return errorObject;
    }

});

export const exportFile = createAsyncThunk(
  "dashboard/exportFile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/ecrves/statistics/export/xlsx`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['Content-Disposition'] || response.headers['contentDisposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `ecrf_statistics_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match.length > 1) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        resetEcrfVisits(state) {
            state.ecrfVisits = initialState.ecrfVisits;
        },
    },
    extraReducers: (builder) => {
        // builder.addCase(fetchEcrves.fulfilled, (state, action) => {
        //   state.ecrves.data = action.payload._embedded?.item || [];
        //   state.ecrves.itemsPerPage = action.payload.itemsPerPage;
        //   state.ecrves.totalItems = action.payload.totalItems;
        // });
        builder.addCase(fetchEcrfVisits.fulfilled, (state: EcrfState, action) => {
            state.ecrfVisits = action.payload;
        });
        builder.addCase(fetchStats.fulfilled, (state: EcrfState, action) => {
            state.stats = action.payload;
        });
        builder.addCase(fetchStatsEcrves.fulfilled, (state: EcrfState, action) => {
            state.statsEcrves = action.payload;
        });
        builder
          .addCase(exportFile.pending, (state) => {
            state.isLoading = true;
            state.error = null;
          })
          .addCase(exportFile.fulfilled, (state) => {
            state.isLoading = false;
          })
          .addCase(exportFile.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
          });
    },
});

export const selectEcrves = (state: RootState) => state.dashboard.dashboard.ecrves;
export const selectEcrfVisits = (state: RootState) => state.dashboard.dashboard.ecrfVisits;
export const selectStats = (state: RootState) => state.dashboard.dashboard.stats;
export const selectStatsEcrves = (state: RootState) => state.dashboard.dashboard.statsEcrves;
export const selectEcrfResourceLoading = (state: RootState) => state.dashboard.dashboard.isLoading;

export const { resetEcrfVisits } = dashboardSlice.actions;

export default dashboardSlice.reducer;
